import { PageHeader } from '../components/common';
import { useDashboardPreview } from '../context/DashboardPreviewContext';
import ExecutiveDashboard from './dashboards/ExecutiveDashboard';
import PlatformOwnerDashboard from './dashboards/PlatformOwnerDashboard';
import ManagerDashboard from './dashboards/ManagerDashboard';
import SupervisorDashboard from './dashboards/SupervisorDashboard';
import LeaderDashboard from './dashboards/LeaderDashboard';
import HRDashboard from './dashboards/HRDashboard';
import FinanceDashboard from './dashboards/FinanceDashboard';
import WarehouseDashboard from './dashboards/WarehouseDashboard';
import PurchasingDashboard from './dashboards/PurchasingDashboard';
import AuditorDashboard from './dashboards/AuditorDashboard';
import SystemAdminDashboard from './dashboards/SystemAdminDashboard';
import type { RoleCode } from '../types/domain';

const TITLES: Record<RoleCode, { title: string; subtitle: string }> = {
  PLATFORM_OWNER: { title: 'Platform Owner Dashboard', subtitle: 'Ringkasan lintas-tenant platform ALEX ERP.' },
  TENANT_OWNER: { title: 'Executive Command Center', subtitle: 'Ringkasan performa bisnis untuk pengambilan keputusan strategis.' },
  MANAGER: { title: 'Manager Dashboard', subtitle: 'Ringkasan operasional harian cabang Anda.' },
  SUPERVISOR: { title: 'Supervisor Dashboard', subtitle: 'Pengawasan operasional cabang — kehadiran, antrean, dan pengecualian stok hari ini.' },
  LEADER: { title: 'Leader Dashboard', subtitle: 'Ringkasan tim shift — kehadiran, cuti, dan serah terima.' },
  FINANCE: { title: 'Finance & Accounting Dashboard', subtitle: 'Ringkasan kas, hutang-piutang, dan jurnal.' },
  PURCHASING: { title: 'Purchasing & Procurement Dashboard', subtitle: 'Ringkasan PR, PO, GR, dan pemasok.' },
  WAREHOUSE: { title: 'Inventory & Receiving Workspace', subtitle: 'Ringkasan penerimaan barang dan stok gudang.' },
  HR: { title: 'HR & Workforce Dashboard', subtitle: 'Ringkasan kehadiran, cuti, dan kedisiplinan karyawan.' },
  AUDITOR: { title: 'Audit & Compliance Dashboard', subtitle: 'Tampilan read-only — jejak audit dan aktivitas tenant.' },
  SYSTEM_ADMIN: { title: 'System Administration', subtitle: 'Administrasi tenant ini — cabang, gudang, konfigurasi, dan aktivitas sistem.' },
  MAINTENANCE: { title: 'Maintenance Dashboard', subtitle: 'Ringkasan aset dan work order perawatan.' },
  MARKETING: { title: 'CRM & Marketing Dashboard', subtitle: 'Ringkasan pelanggan, feedback, dan komplain.' },
};

/**
 * Dashboard router — renders the role-family template selected in
 * DashboardPreviewContext. Each role family has a dedicated workspace
 * backed by the data that role actually works with: TENANT_OWNER gets the
 * real ExecutiveDashboard; PURCHASING gets a procurement-focused
 * dashboard (PR/PO/GR/suppliers); AUDITOR gets a genuinely read-only
 * audit-trail view (no create/edit/approve/post/delete anywhere on it);
 * WAREHOUSE mixes real GR/stock data with an honestly-labeled note about
 * screens not yet built; PLATFORM_OWNER/MANAGER/HR/FINANCE render premium
 * layout previews with a persistent DemoBanner, since those modules have
 * no browser-facing backend yet.
 */
export default function DashboardPage() {
  const { previewRole } = useDashboardPreview();
  const copy = TITLES[previewRole];

  return (
    <div>
      <PageHeader title={copy.title} subtitle={copy.subtitle} />
      <DashboardBody role={previewRole} />
    </div>
  );
}

function DashboardBody({ role }: { role: RoleCode }) {
  switch (role) {
    case 'PLATFORM_OWNER':
      return <PlatformOwnerDashboard />;
    case 'SYSTEM_ADMIN':
      return <SystemAdminDashboard />;
    case 'MANAGER':
      return <ManagerDashboard />;
    case 'SUPERVISOR':
      return <SupervisorDashboard />;
    case 'LEADER':
      return <LeaderDashboard />;
    case 'HR':
      return <HRDashboard />;
    case 'FINANCE':
      return <FinanceDashboard />;
    case 'WAREHOUSE':
      return <WarehouseDashboard />;
    case 'PURCHASING':
      return <PurchasingDashboard />;
    case 'AUDITOR':
      return <AuditorDashboard />;
    case 'TENANT_OWNER':
    default:
      return <ExecutiveDashboard />;
  }
}
